<template>
  <v-form id="AutoAssignmentForm" v-model="formIsValid">
    <filter-bar
      ref="filterBar"
      :allowEditFilters="true"
      :propFilters="filterList"
      :hideFilterOverrideBar="true"
      :is-visible="true"
      :searchable-contexts="['autoAssignment']"
      @updateFilters="updateFilters"
      data-cy="filterList"
      hideQuickFilters
    ></filter-bar>

    <user-picker attributeName="assignee" class="mt-5" label="Assignee" outlined v-model="assignee"> </user-picker>

    <v-alert data-cy="duplicateRuleAlert" v-if="showDuplicateAlert" type="error" outlined>
      This filter duplicates or overlaps with the following existing filter(s):
      <ul class="mt-2">
        <li v-for="(conflict, index) in matchingConflicts" :key="index" class="mb-2">
          <div class="font-weight-medium">
            <template v-for="(filter, fi) in conflict.filters">
              <span :key="'f' + fi">{{ filter }}</span>
              <span v-if="fi < conflict.filters.length - 1" :key="'and' + fi" class="font-italic"> -AND- </span>
            </template>
          </div>
          <div class="text-body-2">Assigned to: {{ formatUsername(conflict.assignee, userMap) }}</div>
        </li>
      </ul>
      <div class="mt-2">Filters must be unique between rules. Please modify filter selection.</div>
    </v-alert>

    <v-alert data-cy="wideRuleAlert" v-else-if="showWideAlert" type="warning" outlined
      >One of your filters is based on an "Excludes" or "!=".<br />Continuum cannot detect whether this filter will
      overlap with existing rules. Please check before saving.</v-alert
    >

    <v-alert v-if="formDirty" type="warning" outlined dense class="mb-3">
      You have unsaved changes — click {{ mode }} to apply them to this rule.
    </v-alert>

    <div>
      <v-btn outlined class="mr-3" @click="closeForm" color="grey darken-4">Cancel</v-btn>
      <v-btn
        :disabled="!isButtonEnabled"
        @click="upsertRuleList"
        color="primary"
        data-cy="actionRuleButton"
        name="createRuleList"
      >
        {{ mode }}
      </v-btn>
    </div>
  </v-form>
</template>

<script>
import FilterBar from "@/components/filterBar";
import UserPicker from "@/components/userPicker";

import { cloneDeep, isEqual } from "lodash";
import { mapActions, mapGetters } from "vuex";
import { formatFilterList, formatUsername } from "@/lib/formatters";

export default {
  components: {
    FilterBar,
    UserPicker,
  },
  computed: {
    isButtonEnabled() {
      return this.filterList.length > 0 && this.assignee?.length > 0 && !this.ruleIsDuplicate && !this.isSubmitting;
    },
    matchingConflicts() {
      const formattedCurrentFilters = formatFilterList(this.filterList);
      return this.ruleList
        .filter(rule => !!this.findMatchInRule(rule, formattedCurrentFilters))
        .map(rule => ({
          filters: rule.filterList.map(f => this.getConflictFilterLabel(f)),
          assignee: rule.assignee,
        }));
    },
    mode() {
      return this.editedIndex > -1 ? "UPDATE" : "CREATE";
    },
    ruleIsDuplicate() {
      return this.matchingConflicts.length > 0;
    },
    ruleList() {
      const { editedIndex, ruleListBase } = this;

      if (editedIndex === -1) {
        return ruleListBase;
      }

      // Exclude the currently-edited rule (for duplicate detection)
      return [].concat(ruleListBase.slice(0, editedIndex), ruleListBase.slice(editedIndex + 1));
    },
    showDuplicateAlert() {
      return this.ruleIsDuplicate && !this.isSubmitting;
    },
    showWideAlert() {
      return !!this.filterList.find(filter => filter.operator === "notIn");
    },
    ...mapGetters({
      ruleListBase: "autoAssignment/getRuleList",
      userMap: "user/getUserMap",
      getDropdownValue: "admin/dropdown/getDropdownValueFromValueList",
    }),
  },
  data() {
    return {
      assignee: "",
      filterList: [],
      formDirty: false,
      formIsValid: false,
      isSubmitting: false,
    };
  },
  methods: {
    formatUsername,
    getConflictFilterLabel(filter) {
      const { attributeName, attributeNameLabel, operator, operatorLabel, value, dataType } = filter;

      let displayValue;
      if (value === null || (Array.isArray(value) && value.length === 1 && value[0] === null)) {
        displayValue = "NONE";
      } else if (
        dataType === "dynamicDropdown" ||
        attributeName === "applicationDropdownValueId" ||
        attributeName === "primaryActivityDropdownValueId"
      ) {
        const ids = Array.isArray(value) ? value : [value];
        displayValue = ids.map(id => this.resolveDropdownId(id)).join(" —OR— ");
      } else if (Array.isArray(value)) {
        displayValue = value.join(" —OR— ");
      } else {
        displayValue = String(value);
      }

      return `${attributeNameLabel || attributeName} ${operatorLabel || operator} ${displayValue}`;
    },
    resolveDropdownId(id) {
      const item = this.getDropdownValue(id);
      if (!item.label) return String(id);

      let label = item.label;
      if (item.groupDropdownValueId) {
        const groupItem = this.getDropdownValue(item.groupDropdownValueId);
        if (groupItem.label) label = `${label} (${groupItem.label})`;
      }
      return label;
    },
    compareFilters_in(f1, f2) {
      if (f1.attributeName !== f2.attributeName) {
        return false;
      }

      const a = f1.value;
      const b = f2.value;

      return !!a.find(aValue => {
        return b.find(bValue => {
          return isEqual(bValue, aValue);
        });
      });
    },
    compareFilters(f1, f2) {
      if (f1.operator === "in" && f2.operator === "in") {
        return this.compareFilters_in(f1, f2);
      }

      return isEqual(f1, f2);
    },
    clearForm() {
      this.$_isPopulating = true;
      this.filterList = [];
      this.formDirty = false;
      this.assignee = null;
      this.$refs.filterBar?.hideForm();
      this.$nextTick(() => { this.$_isPopulating = false; });
    },
    closeForm() {
      this.clearForm();
      this.$emit("closeForm");
    },
    findMatchInRule(rule, filterList) {
      const { compareFilters } = this;
      const { filterList: searchFilterList } = rule;

      return searchFilterList.find(searchFilter => {
        return filterList.find(filter => {
          return !!compareFilters(filter, searchFilter);
        });
      });
    },
    selectUser(user) {
      this.assignee = user;
    },
    updateFilters(filterList) {
      this.filterList = [...filterList];
      this.formDirty = true;
    },
    async upsertRuleList() {
      try {
        this.isSubmitting = true;
        const formattedFilterList = formatFilterList(this.filterList);
        const rule = { assignee: this.assignee, filterList: formattedFilterList };
        if (this.mode === "CREATE") {
          await this.createRule(rule);
          this.$notifySuccess("Rule saved");
          this.$emit("updateEditIndex", -1);
          this.closeForm();
        } else {
          const payload = {
            editIndex: this.editedIndex,
            ruleList: rule,
          };
          await this.updateRule(payload);
          this.$notifySuccess("Rule updated");
          this.$emit("updateEditIndex", -1);
          this.closeForm();
        }
      } catch (e) {
        console.error(e.message);
        this.$notifyError("Failed to save rule. Please try again.");
      } finally {
        this.isSubmitting = false;
      }
    },
    ...mapActions({
      createRule: "autoAssignment/create",
      updateRule: "autoAssignment/update",
    }),
  },
  props: {
    editedIndex: {
      type: Number,
    },
    editedItem: {
      type: Object,
    },
  },
  watch: {
    assignee(newVal, oldVal) {
      if (!this.$_isPopulating && newVal !== oldVal) {
        this.formDirty = true;
      }
    },
    editedItem: {
      handler: function(configuration) {
        if (configuration?.editConfirmed) {
          this.$_isPopulating = true;
          this.formDirty = false;
          this.$refs.filterBar?.hideForm();
          this.filterList = cloneDeep(configuration.filterList);
          this.assignee = cloneDeep(configuration.assignee);
          this.$nextTick(() => { this.$_isPopulating = false; });
        }
      },
      deep: true,
    },
  },
};
</script>

<style></style>
